// =============================================================================
// The shared "get me a copy code" control: a camera scanner plus a text field
// that ALWAYS works. (§2.3)
//
// Both Issue and Return need precisely this, and it is the one control where a
// subtle difference between the two screens would be felt as a bug by staff.
// =============================================================================

import { el, mount } from './dom.js';
import { T } from './strings.js';
import { startScanner, stopScanner, isScanning } from './scanner.js';

let seq = 0;

// A code the camera has already delivered is ignored for this long. The
// decoder's own 2.5s debounce is only meant to stop double-adds; it is far too
// short for a book that has just been rejected or just been returned, where the
// same label sits in frame while staff read the message and the screen would
// otherwise flash the same error every few seconds.
const SCAN_COOLDOWN_MS = 20000;

/**
 * `closeOnScan` switches the camera off the moment a code is read.
 *
 * Return sets it: one scan produces one confirmation card, and leaving the
 * camera running behind it drains the battery, keeps the torch of a phone
 * pointed at the desk, and re-reads the same label the moment the card is
 * dismissed. Issue does NOT set it — scanning a stack of three books is the
 * normal case there, and closing between each one would be worse.
 */
export function createCodeInput({ label, placeholder, actionLabel, onSubmit, autoFocus = false, closeOnScan = false }) {
  const viewportId = `scanner-${++seq}`;
  const viewport = el('div', { id: viewportId, class: 'scanner-viewport hidden' });
  const status = el('p', { class: 'scanner-status hidden' });
  // Stays on the page after the overlay closes — camera errors have to be
  // readable once the camera is gone.
  const note = el('p', { class: 'scanner-status' });

  const input = el('input', {
    type: 'text',
    class: 'code-input',
    placeholder: placeholder || T.bookCodePlaceholder,
    autocomplete: 'off',
    autocapitalize: 'characters',
    spellcheck: 'false',
    'aria-label': label || T.scanOrType,
    // enterkeyhint makes the phone keyboard's action key say "Go", which is
    // what staff will hit after typing a fallback code.
    enterkeyhint: 'go',
  });

  const scanBtn = el('button', { class: 'btn', type: 'button' }, T.scan);
  // MUST be type="submit": inside a <form>, a type="button" does not trigger
  // the submit handler, so tapping it would silently do nothing and only the
  // keyboard Enter key would work.
  const addBtn = el('button', { class: 'btn btn-primary', type: 'submit' }, actionLabel || T.add);

  // Codes the camera has handled recently, so the same label held in frame is
  // not re-submitted. Typed entry is never suppressed: if staff type a code
  // twice they mean it twice.
  const recentScans = new Map();

  const key = (v) => v.trim().toUpperCase();

  function forget(code) { recentScans.delete(key(code)); }
  function remember(code) { recentScans.set(key(code), Date.now()); }

  const submit = () => {
    const value = input.value.trim();
    if (!value) return;
    input.value = '';
    // A typed code clears any cooldown on it, so a re-scan straight afterwards
    // still works.
    forget(value);
    onSubmit(value);
  };

  function fromScan(text) {
    const k = key(text);
    const at = recentScans.get(k);
    if (at && Date.now() - at < SCAN_COOLDOWN_MS) return;
    recentScans.set(k, Date.now());
    // Stop BEFORE handing the code on, so the camera is already down by the
    // time the screen redraws — otherwise a slow lookup leaves the viewport
    // live under the result for as long as the request takes.
    //
    // Unconditional now that the scanner is a full-screen overlay: leaving it
    // open would cover the very result the scan produced. `closeOnScan` is kept
    // for callers that embed the viewport some other way.
    stop();
    onSubmit(text);
  }

  const form = el('form', {
    class: 'code-input-row',
    onSubmit: (e) => { e.preventDefault(); submit(); },
  }, input, addBtn);

  // The camera lives in a full-screen overlay, not inline under the button.
  // Inline, the viewport opened BELOW the fold on a phone: staff tapped Scan,
  // saw nothing change, and had to scroll to find the camera they had just
  // asked for. A scanner is a modal act — nothing else on the screen is usable
  // while it is running — so it takes the screen.
  const stopBtn = el('button', { class: 'btn', type: 'button' }, T.stopScanning);
  stopBtn.addEventListener('click', () => stop());

  const overlay = el('div', {
    class: 'scanner-overlay',
    role: 'dialog',
    'aria-modal': 'true',
    'aria-label': T.scanOrType,
  },
    el('div', { class: 'scanner-panel' },
      viewport,
      status,
      el('div', { class: 'scanner-actions' }, stopBtn),
    ));

  // Tapping the backdrop closes it, the way every other modal on a phone does.
  overlay.addEventListener('click', (e) => { if (e.target === overlay) stop(); });
  const onKey = (e) => { if (e.key === 'Escape') stop(); };

  async function stop() {
    await stopScanner();
    viewport.classList.add('hidden');
    status.classList.add('hidden');
    overlay.remove();                       // not just hidden: navigating between
                                            // screens would otherwise leave a
                                            // dead overlay on <body> each time
    document.body.classList.remove('scanner-open');
    document.removeEventListener('keydown', onKey);
    scanBtn.textContent = T.scan;
  }

  async function start() {
    viewport.classList.remove('hidden');
    status.classList.remove('hidden');
    status.textContent = T.scannerStarting;
    status.className = 'scanner-status';
    scanBtn.textContent = T.stopScanning;

    // On <body>, so the overlay is not trapped inside a scrolling card or a
    // parent with its own stacking context.
    document.body.append(overlay);
    document.body.classList.add('scanner-open');
    document.addEventListener('keydown', onKey);
    queueMicrotask(() => stopBtn.focus());

    await startScanner(
      viewportId,
      fromScan,
      () => {
        // Permission denied, no camera, or insecure context. Say so once and
        // leave the typed field ready — never block the transaction. The
        // message goes INLINE, not in the overlay, because the overlay is about
        // to close and the message has to outlive it.
        stop();
        note.textContent = T.cameraDenied;
        note.className = 'scanner-status scanner-status-warn';
        input.focus();
      },
    );
    if (isScanning()) status.textContent = T.scannerHint;
  }

  scanBtn.addEventListener('click', () => (isScanning() ? stop() : start()));

  // Shown instead of the controls when the caller has closed the input — the
  // borrowing limit being reached, in practice.
  const blocked = el('p', { class: 'notice hidden', role: 'status' });

  const root = el('div', { class: 'code-input-block' },
    el('label', { class: 'field-label' }, label || T.scanOrType),
    el('div', { class: 'code-input-actions' }, scanBtn),
    note,
    blocked,
    form,
  );

  /**
   * Close the control, with the reason shown in place of it. Pass null to
   * reopen.
   *
   * Turning the camera OFF matters as much as disabling the button: a running
   * scanner that silently ignores every code reads as the app being broken,
   * and staff will keep presenting books to it.
   */
  function setDisabled(reason) {
    const off = Boolean(reason);
    input.disabled = off;
    addBtn.disabled = off;
    scanBtn.disabled = off;
    form.classList.toggle('hidden', off);
    scanBtn.classList.toggle('hidden', off);
    blocked.textContent = reason || '';
    blocked.classList.toggle('hidden', !off);
    if (off && isScanning()) stop();
  }

  if (autoFocus) queueMicrotask(() => input.focus());

  // `remember` lets a screen mute a code it has just dealt with — a returned
  // book is available a moment later, and re-scanning it would say "not on
  // loan" for as long as the label stayed in frame.
  return { root, stop, focus: () => input.focus(), remember, forget, setDisabled };
}
